import { KInput, KButton, KTooltip, KDropdown, KSpan } from "kahuna-base-react-components"
import React, { useEffect, useState } from "react"
import FilterOptionsDialog from "./FilterOptionsDialog"
import { useNavigate } from "react-router-dom"
import { lang } from "../../../constants/languages"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import DiscoveryModeModal from "./discovery-mode/DiscoveryModeModal"
import { discoveryMode } from "../../../apis"
import { DiscoveryModeCampaign } from "../../../types"
import { formatIsoDate, mapToCamelCase } from "../../../utility"

const searchOptions: KSelectOption[] = [
  { label: "Release", value: 0, value2: "title" },
  { label: "Artist", value: 1, value2: "artist" },
  { label: "UPC", value: 2, value2: "upc" },
  { label: "Label", value: 3, value2: "label" },
  { label: "ISRC", value: 4, value2: "isrc" },
  { label: "Feat Artist", value: 5, value2: "featuring" }
]

const CatalogFilterComponents = ({
  onSearchRelease,
  newReleasePermission,
  initialSearchText,
  initialSearchKey,
  discoveryModePermission
}: {
  onSearchRelease: (searchText: string, searchKey: string) => void
  newReleasePermission: boolean
  initialSearchText?: string
  initialSearchKey?: string
  discoveryModePermission: boolean
}) => {
  const [searchText, setSearchText] = useState<string>(initialSearchText || "")
  const [selectedSearchOption, setSelectedSearchOption] = useState<KSelectOption>(
    searchOptions.find((option) => option.value2 === initialSearchKey) || searchOptions[0]
  )

  const handleChange = (newValue: string) => {
    setSearchText(newValue)
    onSearchRelease(newValue, selectedSearchOption?.value2 || "title")
  }

  const [openFilters, setOpenFilters] = useState<boolean>(false)
  const [searchOpen, setSearchOpen] = useState(!!initialSearchText)
  const [openDiscoveryMode, setOpenDiscoveryMode] = useState(false)
  const [discoveryModeCampaign, setDiscoveryModeCampaign] = useState<DiscoveryModeCampaign | undefined>(undefined)

  const navigate = useNavigate()

  // The open campaign drives both the info box on the button and the modal's initial selection.
  useEffect(() => {
    if (!discoveryModePermission) return

    discoveryMode("/current_campaign/", "get")
      .then((response) => setDiscoveryModeCampaign(mapToCamelCase<DiscoveryModeCampaign>(response.data)))
      .catch(() => setDiscoveryModeCampaign(undefined))
  }, [discoveryModePermission])

  const renderDiscoveryModeTooltip = () => (
    <div className="flex flex-col gap-1" style={{ maxWidth: 260 }}>
      <KSpan text={lang.catalog.discovery_mode.title} color="#FFF" fontWeight={500} />
      {discoveryModeCampaign && (
        <KSpan
          color="#FFF"
          fontSize={12}
          lineHeight="16px"
          text={lang.catalog.discovery_mode.deadline_info
            .replace("<deadline>", formatIsoDate(discoveryModeCampaign.deadlineDate))
            .replace("<campaign>", discoveryModeCampaign.campaignLabel)}
        />
      )}
    </div>
  )

  return (
    <>
      <div className="flex flex-row justify-between gap-3 items-center pr-3">
        {searchOpen ? (
          <div
            className="flex items-center py-[13px] pl-3.5 pr-3 gap-2 rounded-[10px]"
            style={{
              background: searchText ? "#FFF" : "#F7F7F7",
              border: "1px solid #F3F3F3",
              height: "48px",
              transition: "0.3s",
              width: 370
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setSearchOpen(false)
              }
            }}
          >
            <KInput
              value={searchText}
              padding="0px"
              background="transparent"
              onChange={handleChange}
              leftIcon="/catalog_icons/search-grey.svg"
              gap="8px"
              width={200}
              borderRadius={0}
              shadowDisabled
              placeholder={lang.catalog.filter_components.search_text_placeholder}
            />
            <KDropdown
              rightIcon="/admin_icons/caret-down.svg"
              selected={selectedSearchOption}
              options={searchOptions}
              onSelect={(selected) => {
                const selectedOption = selected as KSelectOption
                setSelectedSearchOption(selectedOption)
                if (searchText) {
                  onSearchRelease(searchText, selectedOption?.value2 || "title")
                }
              }}
              padding="8px"
              background="transparent"
              shadowDisabled={true}
              enableIndicator={true}
              allowContainerShrink={false}
              menuWidth={170}
              width={170}
              textColor="#F3F3F3"
            />
          </div>
        ) : (
          <KTooltip content={lang.catalog.filter_components.search_text_tooltip} position="bottom">
            <KButton
              leftIcon="/catalog_icons/search-filled.svg"
              background="#F7F7F7"
              onClick={() => setSearchOpen(true)}
              hoverBackground="#F3F3F3"
              padding="14px"
              width="auto"
              height="auto"
            />
          </KTooltip>
        )}

        <KTooltip content={lang.catalog.filter_components.filters_tooltip} position="bottom">
          <KButton
            leftIcon="/catalog_icons/filters.svg"
            background="#F7F7F7"
            onClick={() => {
              setOpenFilters(true)
            }}
            hoverBackground="#F3F3F3"
            padding="14px"
            width="auto"
            height="auto"
          />
        </KTooltip>

        {discoveryModePermission && (
          <KTooltip
            backgroundColor="#595959"
            width="300px"
            position="bottom"
            align="center"
            content={renderDiscoveryModeTooltip()}
          >
            <KButton
              dataTestId="discovery-mode-button"
              leftIcon="/account_icons/spotify_small.svg"
              background="#F7F7F7"
              onClick={() => setOpenDiscoveryMode(true)}
              hoverBackground="#F3F3F3"
              padding="10px"
              width="auto"
              height="auto"
            />
          </KTooltip>
        )}

        <KTooltip content={lang.catalog.filter_components.release_plan_tooltip} position="bottom">
          <KButton
            dataTestId="release-plan-button"
            leftIcon="/catalog_icons/music-list.svg"
            background="#F7F7F7"
            onClick={() => navigate(`/catalog/release-plan`)}
            hoverBackground="#F3F3F3"
            width="auto"
            height="auto"
            padding="14px"
          />
        </KTooltip>

        {newReleasePermission && (
          <KTooltip content={lang.catalog.filter_components.new_release_tooltip} position="bottom">
            <KButton
              leftIcon="/catalog_icons/plus-filled.svg"
              background="#F7F7F7"
              onClick={() => navigate(`/catalog/new-release`)}
              hoverBackground="#F3F3F3"
              width="auto"
              height="auto"
              padding="14px"
            />
          </KTooltip>
        )}
      </div>
      <FilterOptionsDialog openFilters={openFilters} setOpenFilters={setOpenFilters}></FilterOptionsDialog>
      {discoveryModePermission && (
        <DiscoveryModeModal
          open={openDiscoveryMode}
          campaign={discoveryModeCampaign}
          onClose={() => setOpenDiscoveryMode(false)}
          onSubmitted={setDiscoveryModeCampaign}
        />
      )}
    </>
  )
}

export default CatalogFilterComponents
